
import { AuthService, UserRole } from '@/lib/services/auth-service'
import { useAuthStore } from '@/lib/store/auth'
import { Logger } from '@/lib/utils/logger'
import { CaregiverService } from '@/lib/services/caregiver-service'
import { useCaregiverStore } from '@/lib/store/caregiver'
import { supabase } from '@/integrations/supabase/client'

const logger = new Logger('useSignIn')

async function fetchSpecialistId(userId: string): Promise<string | null> {
  try {
    logger.info('Fetching specialist ID', { userId })
    const { data, error } = await supabase
      .from('specialists')
      .select('id')
      .eq('auth_user_id', userId)
      .maybeSingle()

    if (error) {
      logger.error('Error fetching specialist ID', { error })
      throw new Error('Could not verify specialist credentials')
    }

    if (!data?.id) {
      logger.error('No specialist ID found', { userId })
      throw new Error('Could not verify specialist credentials')
    }

    return data.id
  } catch (error) {
    logger.error('Unexpected error fetching specialist ID', { error })
    throw error
  }
}

export async function signIn(email: string, password: string, role: UserRole) {
  logger.info('Starting sign in process', { email, role })
  
  try {
    // Reset any previous state
    useAuthStore.setState({ loading: true, error: null })

    // Attempt to sign in
    const { success, session, error } = await AuthService.signIn(email, password, role)

    if (!success || error || !session) {
      throw new Error(error || 'Sign in failed')
    }

    logger.info('Authentication successful', { userId: session.user.id, role })

    // Handle role-specific data fetching
    if (role === 'specialist') {
      try {
        const specialistId = await fetchSpecialistId(session.user.id)
        logger.info('Setting specialist session', { specialistId })
        
        await useAuthStore.getState().setAuthData({
          ...session,
          specialistId,
          role: 'specialist'
        })
      } catch (error) {
        logger.error('Failed to set specialist auth data', { error })
        throw new Error('Could not verify specialist credentials. Please contact support.')
      }
    } 
    else if (role === 'caregiver') {
      const caregiver = await CaregiverService.getCurrentCaregiver()
      
      if (!caregiver) {
        logger.warn('No caregiver profile found', { userId: session.user.id })
      }

      logger.info('Setting caregiver session', { caregiverId: caregiver?.id })
      await useAuthStore.getState().setAuthData({
        ...session,
        role: 'caregiver'
      })
      
      if (caregiver) {
        useCaregiverStore.getState().setCaregiverId(caregiver.id)
      }
    }

    return { 
      data: { session },
      error: null 
    }
  } catch (err) {
    logger.error('Sign in process failed', { error: err })
    
    // Construct user-friendly error message
    const errorMessage = err instanceof Error 
      ? err.message 
      : 'An unexpected error occurred. Please try again later.'
    
    // Clean up: sign out and clear stores
    try {
      await AuthService.signOut()
    } catch (signOutError) {
      logger.error('Error during cleanup signout', { error: signOutError })
    }
    
    // Reset auth store to initial state
    useAuthStore.setState({
      error: errorMessage,
      loading: false,
      user: null,
      session: null,
      role: null,
      accessToken: null,
      refreshToken: null,
      userMetadata: null,
      specialistId: null
    })
    
    // Reset caregiver store
    useCaregiverStore.getState().setCaregiverId(null)
    
    throw err
  } finally {
    useAuthStore.setState({ loading: false })
  }
}
